import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Alert, Button, Card, Empty, Skeleton} from "antd";
import dayjs from "dayjs";
import {ComponentPropsFromRoute} from "../../../../routers/routers";
import {HealthCheckGroup} from "../../../../models/health-checker/health-checker";
import {healthCheckerService} from "../../../../services/health-checker/health-checker.service";
import {useTranslate} from "../../../../hooks/translate.hook";
import {columnsFactory} from "./dashboard.columns";
import {DashboardToolbar} from "./dashboard.toolbar";
import {DashboardSummary} from "./dashboard.summary";
import {DashboardIssues} from "./dashboard.issues";
import {DashboardGroups} from "./dashboard.groups";
import {HealthCheckWithGroup} from "./dashboard.types";
import "./dashboard.scss";

export const HealthCenterDashboard: React.FC<ComponentPropsFromRoute> = () => {
    const t = useTranslate();
    const [groups, setGroups] = useState<HealthCheckGroup[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<boolean>(false);
    const [loadedAt, setLoadedAt] = useState<string>();

    const loadChecks = useCallback(async () => {
        setLoading(true);
        setLoadError(false);

        try {
            const response = await healthCheckerService.getChecksByGroups();
            setGroups(response);
            setLoadedAt(dayjs().format("DD.MM.YYYY HH:mm:ss"));
        } catch {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadChecks();
    }, [loadChecks]);

    const checks = useMemo<HealthCheckWithGroup[]>(() => groups.flatMap((group) =>
        group.checks.map((check) => ({
            ...check,
            groupName: group.name,
        }))), [groups]);

    const failedChecks = useMemo(
        () => checks.filter((check) => check.status === "not_ok"),
        [checks]
    );

    const columns = useMemo(() => columnsFactory(t), [t]);
    const initialLoading = loading && groups.length === 0;

    return (
        <div className="health-center-dashboard">
            <DashboardToolbar
                loading={loading}
                loadedAt={loadedAt}
                onRefresh={loadChecks}
            />

            {loadError ? (
                <Alert
                    className="health-center-dashboard__load-error"
                    type="error"
                    showIcon
                    message={t("HEALTH-CENTER.DASHBOARD.LOAD-ERROR", {
                        defaultValue: "Could not load health checks",
                    })}
                    description={t("HEALTH-CENTER.DASHBOARD.LOAD-ERROR-DESCRIPTION", {
                        defaultValue: "Try refreshing the dashboard.",
                    })}
                    action={
                        <Button size="small" danger onClick={() => void loadChecks()}>
                            {t("HEALTH-CENTER.DASHBOARD.RETRY", {defaultValue: "Retry"})}
                        </Button>
                    }
                />
            ) : null}

            {initialLoading ? (
                <Card>
                    <Skeleton active paragraph={{rows: 8}} />
                </Card>
            ) : null}

            {!initialLoading && checks.length === 0 && !loadError ? (
                <Card>
                    <Empty
                        description={t("HEALTH-CENTER.DASHBOARD.NO-CHECKS", {
                            defaultValue: "No health checks configured",
                        })}
                    />
                </Card>
            ) : null}

            {!initialLoading && checks.length > 0 ? (
                <>
                    <DashboardSummary
                        groupsCount={groups.length}
                        checksCount={checks.length}
                        healthyChecksCount={checks.length - failedChecks.length}
                        failedChecksCount={failedChecks.length}
                    />
                    <DashboardIssues checks={failedChecks} />
                    <DashboardGroups groups={groups} columns={columns} />
                </>
            ) : null}
        </div>
    );
};
